import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ConfirmationModalService } from '@epicuro-next/ataraxia/modal';
import { dynamicElementsSpotlightListsKey as dynamicElementsSpotlightListsKey } from '@epicuro-next/constants/dynamic-elements';
import {
  ProductListConfigurationKeys,
  PRODUCT_LIST_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/product-list-rules';
import { EngagementFeedWithUser } from '@epicuro-next/data/engagement/engagement-models';
import { DontTreeShake } from '@epicuro-next/elements/core';
import { NewsFeedBaseComponent } from '@epicuro-next/features/engagement/article-common';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { ConfigurationsService } from '@epicuro-next/platform/configuration';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import {
  AnalyticsSource,
  ProductListAnalyticsEvent,
} from '@epicuro-next/platform/event-tracker';
import { AnalyticsService } from '@epicuro-next/platform/monitoring';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import {
  selectProductSource,
  setProductSource,
} from '@epicuro-next/state/config-strip-state';
import {
  actions,
  selectActiveArticle,
  selectAllLoaded,
  selectArticles,
  selectIsLoadingArticles,
  selectLoadFailed,
  selectShowSpotlightScrollButtons,
  selectTotal,
} from '@epicuro-next/state/news-feed-state';
import { selectRouteData } from '@epicuro-next/state/router-store';
import { selectIsAdmin, selectUserId } from '@epicuro-next/state/user-state';
import { verticalCollapse } from '@epicuro-next/ui';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import {
  delay,
  filter,
  map,
  shareReplay,
  startWith,
  take,
  takeUntil,
} from 'rxjs/operators';

import { fade } from '../../animations/fade.animation';

@DynamicComponentRegister(dynamicElementsSpotlightListsKey, true)
@Component({
  selector: 'epc-spotlight',
  templateUrl: './spotlight.component.html',
  styleUrls: ['./spotlight.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [verticalCollapse('*', '0'), fade],
  standalone: false,
})
export class SpotlightComponent
  extends NewsFeedBaseComponent
  implements OnInit, OnDestroy
{
  @Input() public ignoreRouteLogicToShowSpotlight = false;
  @Input() public wheelHorizontalScrollEnabled = true;

  public articles$ = this.store.pipe(
    select(selectArticles),
    takeUntil(this.destroy$),
  );

  public scrollIntoViewProps: ScrollIntoViewOptions = {
    inline: 'center',
    block: 'end',
    behavior: 'smooth',
  };

  public isAdmin$ = this.store.pipe(
    select(selectIsAdmin),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public endOfFeed$: Observable<boolean>;
  public loading$: Observable<boolean>;
  public showSpotlight$: Observable<boolean>;
  public showSpotlightScrollButtons$ = this.store.select(
    selectShowSpotlightScrollButtons,
  );
  public activeArticle$: Observable<EngagementFeedWithUser | null> =
    this.store.pipe(
      select(selectActiveArticle),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  public newItems: string[] = [];
  public isProductListReachedEventEnabled$ =
    this.configurationService.getConfigValue({
      namespace: PRODUCT_LIST_NAMESPACE,
      featureName: ProductListConfigurationKeys.EnableListReachedEvent,
      defaultValue: false,
    });

  private currentProductSource$ = this.store.pipe(
    select(selectProductSource),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private accountId$ = this.store.pipe(
    select(selectActiveAccountId),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private userId$ = this.store.pipe(
    select(selectUserId),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Dependencies */
  constructor(
    private stateActions$: Actions,
    private cdr: ChangeDetectorRef,
    private analytics: AnalyticsService,
    private configurationService: ConfigurationsService,
    public store: Store,
    public confirmationService: ConfirmationModalService,
  ) {
    super(store, confirmationService);
  }

  /** Angular's OnInit life cycle hook */
  public ngOnInit() {
    this.subscribeIsLoading();
    this.subscribeAllLoaded();
    this.subscribeShowSpotlight();
    this.subscribeUpdates();
    this.setProductSource();
  }

  /** Angular's OnDestroy life cycle hook */
  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  /** Method to load more articles */
  public loadArticles() {
    combineLatest([this.endOfFeed$, this.loading$])
      .pipe(take(1))
      .subscribe(([endOfFeed, loading]) => {
        if (endOfFeed || loading) {
          return;
        }
        this.store.dispatch(actions.fetch());
      });
  }

  private setProductSource() {
    combineLatest([
      this.currentProductSource$,
      this.activeArticle$,
      this.articles$,
    ])
      .pipe(
        filter(([source, active]) => !source && !!active),
        map(([, active, list]) => list.find((item) => item.id === active?.id)),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((active) => {
        if (active) {
          this.store.dispatch(
            setProductSource({
              source: BasketItemSource.LIST_SPOTLIGHT,
              details: {
                listId: [active.referenceId],
              },
            }),
          );
        }
      });
  }

  /** Articles track function */
  public trackByFn(_: number, item: EngagementFeedWithUser) {
    return item.id;
  }

  public onArticleClick(article: EngagementFeedWithUser) {
    this.store.dispatch(
      setProductSource({
        source: BasketItemSource.LIST_SPOTLIGHT,
        details: {
          listId: [article.referenceId],
        },
      }),
    );
    this.navigateToArticle(article);
  }

  public togglePinState(value: { id: string; pin: boolean }) {
    if (value.pin) {
      this.store.dispatch(actions.pinArticle({ id: value.id }));
      return;
    }

    this.store.dispatch(actions.unpinArticle({ id: value.id }));
  }

  public isNotSeen(id: string): boolean {
    return this.newItems.indexOf(id) > -1;
  }

  public markAsSeen(id: string) {
    of(id)
      .pipe(delay(3000), take(1))
      .subscribe(() => {
        this.newItems = this.newItems.filter((item) => item !== id);
        this.cdr.detectChanges();
      });
  }

  public onVisible(isVisible: boolean, article: EngagementFeedWithUser) {
    if (article.referenceId && isVisible) {
      combineLatest([this.userId$, this.accountId$.pipe(filter(isNonNullish))])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([userId, accountId]) => {
          this.analytics.trackEvent({
            name: ProductListAnalyticsEvent.ProductListReached,
            properties: {
              source: AnalyticsSource.Spotlight,
              type: ProductListAnalyticsEvent.ProductListReached,
              accountId,
              userId,
              productListId: article.referenceId as string,
              ownerId: article.userId,
            },
          });
        });
    }
  }

  public deleteSpotlightItem(article: EngagementFeedWithUser) {
    this.deleteArticle(article);
  }

  private subscribeAllLoaded() {
    this.endOfFeed$ = this.store.pipe(
      select(selectAllLoaded),
      startWith(true),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this.destroy$),
    );
  }

  private subscribeIsLoading() {
    this.loading$ = this.store.pipe(
      select(selectIsLoadingArticles),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this.destroy$),
    );
  }

  private subscribeShowSpotlight() {
    const showBasedOnRoute$ = this.ignoreRouteLogicToShowSpotlight
      ? of(true)
      : this.store.pipe(
          select(selectRouteData),
          map((data) => data?.showSpotlight ?? true),
        );

    this.showSpotlight$ = combineLatest([
      showBasedOnRoute$,
      this.endOfFeed$,
      this.store.pipe(select(selectTotal)),
      this.store.pipe(select(selectLoadFailed)),
    ]).pipe(
      map(([showSpotlight, endOfFeed, total, loadFailed]) => {
        if (!showSpotlight || loadFailed) {
          return false;
        }

        return !(endOfFeed && !total);
      }),
      takeUntil(this.destroy$),
    );
  }

  private subscribeUpdates() {
    this.stateActions$
      .pipe(
        ofType(actions.fetchUpdatesSuccess),
        filter(({ data }) => Boolean(data.items.length)),
        map(({ data }) => data),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => {
        this.newItems = [
          ...this.newItems,
          ...data.items.map((item) => item.id),
        ];
      });
  }
}
DontTreeShake(SpotlightComponent);
